import { MIPCreateContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/MIPCreateContext';
import { renderMIPCreateSectionHeaderPortal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/mipCreatePortals';
import { MIP_CREATE_ROUTES } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/useMIPCreateController';
import {
	SAVE_FAILED,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	PermissionRoleWithOwnerEnum,
	ProgrammeUserWithPermissionRolesRes,
} from '@dealstack/shared/lib/generated';
import { useContext, useEffect, useState } from 'react';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { setPermissionsForResourceType } from '@dealstack/frontend/shared/dataExchange/services/permission.api';
import { ProgrammeUserRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/Collaborate/Collaborate.types';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { HeaderCell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import {
	BodyRow,
	DefaultTable,
	HeaderRow,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable';
import {
	useTranslationMipCreate,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import styled from 'styled-components';
import { CollaboratorRow } from './components/CollaboratorRow';
import {
	useDeleteProgrammeUser,
	useInviteProgrammeUser,
	useProgrammeUsers,
	useUpdateProgrammeUser,
} from '@dealstack/frontend/app/pages/Project/MIP/hooks';

const USER_PROGRAMME_TEMPLATE: ProgrammeUserRow = {
	id: '',
	firstName: '',
	lastName: '',
	email: '',
	active: false,
	permissionRoles: [PermissionRoleWithOwnerEnum.VIEWER],
	isContactPerson: false,
	userProgrammeId: '',
	deleted: false,
	canBeRemoved: true,
};

interface Props {
	className?: string;
}

const Collaborate = ({ className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const tMipManage = useTranslationMipManage();

	const queryClient = useQueryClient();

	const deleteUserProgrammeRequest = useDeleteProgrammeUser();
	const inviteTeamMemberRequest = useInviteProgrammeUser();
	const updateUserProgrammeRequest = useUpdateProgrammeUser();
	const setUserProgrammePermissionRequest = useMutation({
		mutationFn: (variables: Parameters<typeof setPermissionsForResourceType>[0]) =>
			setPermissionsForResourceType(variables),
	});

	const mipCreateController = useContext(MIPCreateContext);

	const [programmeUsersList, setProgrammeUsersList] = useState<
		(ProgrammeUserWithPermissionRolesRes & {
			isDeleted?: boolean;
			hasUpdatedPermissions?: boolean;
			hasUpdatedData?: boolean;
		})[]
	>([]);

	const { data: programmeUsersData } = useProgrammeUsers({
		programmeId: mipCreateController.mipId!,
	});

	useEffect(() => {
		if (programmeUsersData) {
			setProgrammeUsersList(programmeUsersData);
		}
	}, [programmeUsersData]);

	const onAddRow = () => {
		setProgrammeUsersList((prevState) => [...prevState, USER_PROGRAMME_TEMPLATE]);
	};

	const onUpdateRow = (updatedUserProgrammeData: ProgrammeUserRow, rowIndex: number) => {
		setProgrammeUsersList((prev) =>
			prev.map((participant, index) => {
				return index === rowIndex
					? { ...participant, ...updatedUserProgrammeData }
					: participant;
			})
		);
	};

	const onSubmit = async (): Promise<WizardSaveResult> => {
		let result: WizardSaveResult = SAVE_OK;
		const onFail = () => {
			mipCreateController?.updateBackupData(MIP_CREATE_ROUTES.COLLABORATE, {
				userProgrammeList: programmeUsersList,
			});
			result = SAVE_FAILED;
		};

		const mipId = mipCreateController?.mipId;

		if (!mipId) {
			showToastV2({
				title: tMipCreate('collaborate.alerts.error'),
				state: 'error',
			});
			onFail();
			return result;
		}

		const emails = new Set<string>();
		const isEmailRepeat = programmeUsersList?.some((participant) => {
			const email = participant.email;
			if (emails.has(email)) {
				return true;
			}

			emails.add(email);
			return false;
		});

		if (isEmailRepeat) {
			showToastV2({
				title: tMipCreate('collaborate.alerts.error'),
				message: tMipCreate('collaborate.alerts.email_repeat'),
				state: 'error',
			});
			onFail();
			return result;
		}

		const promiseReq: Promise<unknown>[] = [];
		let isAnyFailed = false;

		// Function to handle deletion of a user from the program
		const handleDeleteRequests = (user: ProgrammeUserRow) => {
			promiseReq.push(
				deleteUserProgrammeRequest.mutateAsync(
					{ userId: user.id, programmeId: mipId },
					{ onError: () => (isAnyFailed = true) }
				)
			);
		};

		//	 This function converts the type because, in the frontend, we merge "read" values (which include the Owner relation) with "write" values (which don’t).
		// Unfortunately, these types are defined using an enum, so we can't just extend or modify them directly.
		const convertToPermissionRolesType = (
			roles: PermissionRoleWithOwnerEnum[]
		): PermissionRole[] => {
			return roles.filter(
				(item) => item !== PermissionRoleWithOwnerEnum.OWNER
			) as unknown as PermissionRole[];
		};

		const handleAddingNewUsersToProgrammeReq = (user: ProgrammeUserRow) => {
			const { email, firstName, lastName, permissionRoles, isContactPerson } = user;

			promiseReq.push(
				inviteTeamMemberRequest.mutateAsync(
					{
						email,
						firstName,
						lastName,
						programmeId: mipId,
						isContactPerson,
						permissionRoles: convertToPermissionRolesType(permissionRoles),
						options: {
							skipEmail: true,
						},
					},
					{ onError: () => (isAnyFailed = true) }
				)
			);
		};

		// Function to handle permission updates
		const handlePermissionUpdateReq = (user: ProgrammeUserRow) => {
			if (user.hasUpdatedPermissions) {
				promiseReq.push(
					setUserProgrammePermissionRequest.mutateAsync(
						{
							params: { resourceType: ResourceType.PROGRAMME, resourceId: mipId },
							body: {
								permissionRoles: convertToPermissionRolesType(user.permissionRoles),
								userId: user.id,
							},
						},
						{ onError: () => (isAnyFailed = true) }
					)
				);
			}
		};

		// Function to handle data updates
		const handleUserDataUpdateRequest = async (user: ProgrammeUserRow) => {
			if (user.hasUpdatedData) {
				promiseReq.push(
					updateUserProgrammeRequest.mutateAsync(
						{
							firstName: user.firstName,
							lastName: user.lastName,
							isContactPerson: user.isContactPerson,
							userId: user.id,
							programmeId: mipId,
						},
						{ onError: () => (isAnyFailed = true) }
					)
				);
			}
		};

		// Iterate over collaborators list and process accordingly
		for (const programmeUserRowEntry of programmeUsersList) {
			if (programmeUserRowEntry.isDeleted) {
				handleDeleteRequests(programmeUserRowEntry);
				continue;
			}

			if (!programmeUserRowEntry.userProgrammeId) {
				if (!programmeUserRowEntry.email) {
					// disregard users not saved
					continue;
				}
				handleAddingNewUsersToProgrammeReq(programmeUserRowEntry);
				continue;
			}

			handlePermissionUpdateReq(programmeUserRowEntry);
			await handleUserDataUpdateRequest(programmeUserRowEntry);
		}

		await Promise.allSettled(promiseReq);

		if (isAnyFailed) {
			showToastV2({
				title: tMipCreate('collaborate.alerts.failed_save'),
				state: 'error',
			});

			onFail();
		} else {
			await queryClient.invalidateQueries({
				queryKey: [API_PATHS.MIP_PROGRAMMEID_USERS_GET, { programmeId: mipId }],
			});
		}
		return result;
	};

	useWizardStep(onSubmit);

	return (
		<div className={className}>
			{renderMIPCreateSectionHeaderPortal(
				<SectionHeader
					title={tMipCreate('collaborate.header.title')}
					subtitle={tMipCreate('collaborate.header.subtitle')}
				/>
			)}
			<SectionHeader title={tMipCreate('collaborate.table.title')} />
			<DefaultTable maxSize withEditableRows>
				<HeaderRow>
					<HeaderCell size={0.5}>#</HeaderCell>
					<HeaderCell customSize="1fr">
						{tMipManage('settings_modal.first_name')}
					</HeaderCell>
					<HeaderCell customSize="1fr">
						{tMipManage('settings_modal.last_name')}
					</HeaderCell>
					<HeaderCell customSize="2fr">
						{tMipCreate('collaborate.table.email')}
					</HeaderCell>
					<HeaderCell customSize="0.8fr">
						{tMipCreate('collaborate.table.rights')}
					</HeaderCell>
					<HeaderCell
						customSize="1.5fr"
						childrenTooltip={tMipCreate('collaborate.table.contact_person_tooltip')}
					>
						{tMipCreate('collaborate.table.contact_person')}
					</HeaderCell>
					<HeaderCell size={1.8} />
				</HeaderRow>
				<BodyRow>
					{programmeUsersList.map((user, index) => {
						if (user.isDeleted) {
							return null;
						}

						return (
							<CollaboratorRow
								key={`${user.id}${index}`}
								user={user}
								index={index}
								onUpdateRow={onUpdateRow}
							/>
						);
					})}
				</BodyRow>
			</DefaultTable>
			<AddNewUserToProgramme
				data-testid={TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_ADD_NEW_COLLABORATOR_BUTTON}
				onClick={onAddRow}
				iconSize="lg"
				icon="add-circle"
				iconPlacement="left"
				size="medium"
				variant="text"
			>
				{tMipCreate('collaborate.table.add_another')}
			</AddNewUserToProgramme>
		</div>
	);
};

const AddNewUserToProgramme = styled(Button)``;
const Styled = styled(Collaborate)`
	margin-bottom: 3em;

	${DefaultTable}, ${AddNewUserToProgramme} {
		margin: 1em 0;
	}

	${DefaultTable} {
		overflow: visible;
	}

	${HeaderRow} {
		align-items: center;
	}
`;

export { Styled as Collaborate };
